import { registerComponent, handleize } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "predictive-search-submit",
  class PredictiveSearchSubmit extends WYATTElement {
    constructor() {
      super();

      this.redirect = null;
      this.redirectSource = null;
      this.redirectHandle = null;
      this.redirectResponseId = null;
      this.brandChecks = new Map();
      this.submitting = false;
      this.form = this.closest("form");

      this.submitHandler = this.handleSubmit.bind(this);

      this.query = this.querySelector("[data-query]");
    }

    connectedCallback() {
      this.subscribe(
        WYATT.PubSub.events.searchInputChange,
        this.handleSearchInputChange.bind(this),
      );

      this.subscribe(
        WYATT.PubSub.events.redirectFetched,
        this.conditionallSetSearchspringRedirect.bind(this),
      );

      this.subscribe(
        WYATT.PubSub.events.searchInputUnboundChange,
        this.conditionallySetBrandRedirect.bind(this),
      );
    }

    handleSearchInputChange(event) {
      event.preventDefault();

      this.redirect = null;
      this.redirectSource = null;
      this.redirectHandle = null;
      this.redirectResponseId = null;

      const query = event.detail;

      this.query.textContent = `'${query}'`;
    }

    conditionallySetBrandRedirect(event) {
      const query = event.detail;

      if (!query) {
        this.removeRedirect();
        return;
      }

      const handleizedQuery = handleize(query);

      if (WYATT.shopify.brands.includes(handleizedQuery)) {
        this.redirect = `${WYATT.routes.root}collections/${handleizedQuery}`;
        this.redirectSource = "brand";
        this.redirectHandle = handleizedQuery;
        this.redirectResponseId = null;
        // Start the existence check now so it has resolved by the time the
        // shopper presses Enter.
        this.checkBrandCollection(handleizedQuery);
        this.setRedirect();
      } else {
        this.removeRedirect();
      }
    }

    // Replaces the per-render Liquid existence check: collections[handle] for
    // every vendor on every page load stalled first paint site-wide, and the
    // result is only needed here, for a submitted brand query. Memoized per
    // handle, but only settled verdicts persist: a failed or timed-out probe
    // resolves "no collection" for the in-flight submit (the shopper lands on
    // the regular search results, which always work) and is evicted so a
    // later attempt can probe again. The timeout keeps a stalled connection
    // from deadening the Enter key, since the submit handler awaits this.
    checkBrandCollection(handle) {
      if (!this.brandChecks.has(handle)) {
        this.brandChecks.set(
          handle,
          fetch(`${WYATT.routes.root}collections/${handle}.json`, {
            signal: AbortSignal.timeout ? AbortSignal.timeout(2000) : undefined,
          })
            .then((response) => response.ok)
            .catch(() => {
              this.brandChecks.delete(handle);
              return false;
            }),
        );
      }

      return this.brandChecks.get(handle);
    }

    conditionallSetSearchspringRedirect(event) {
      event.preventDefault();

      this.redirect = event?.detail?.merchandising?.redirect;

      if (this.redirect) {
        this.redirectSource = "searchspring";
        this.redirectResponseId = event?.detail?.responseId || "";
        this.setRedirect();
      } else {
        this.removeRedirect();
      }
    }

    setRedirect() {
      this.form.addEventListener("submit", this.submitHandler);
    }

    removeRedirect() {
      this.redirectSource = null;
      this.redirectHandle = null;
      this.redirectResponseId = null;
      this.form.removeEventListener("submit", this.submitHandler);
    }

    async handleSubmit(event) {
      event.preventDefault();

      // The debounced input handlers mutate the redirect fields while the
      // brand probe below is awaited, so act on what was armed at the moment
      // the shopper submitted, not on whatever the fields hold afterwards.
      const { redirect, redirectSource, redirectHandle, redirectResponseId } =
        this;

      if (!redirect || this.submitting) {
        return;
      }

      this.submitting = true;

      // A vendor handle is not guaranteed to be a published collection; fall
      // through to the regular search results page when it is not.
      if (redirectSource === "brand") {
        const collectionExists =
          await this.checkBrandCollection(redirectHandle);

        if (!collectionExists) {
          this.redirect = null;
          // removeRedirect() detaches this handler first, so the re-submit
          // below runs the form's native path. requestSubmit() rather than
          // submit() so other submit listeners (analytics) still see it.
          this.removeRedirect();

          if (this.form.requestSubmit) {
            this.form.requestSubmit();
          } else {
            this.form.submit();
          }

          this.submitting = false;
          return;
        }
      }

      // Only Searchspring-driven redirects are attributable to a Beacon
      // response; brand-handle redirects have no responseId.
      if (
        redirectSource === "searchspring" &&
        WYATT.config.searchspring.beaconV2Enabled &&
        WYATT.beacon
      ) {
        WYATT.beacon.autocomplete.redirect({
          responseId: redirectResponseId,
          redirect,
        });
      }

      // The dropdown redirects straight to the destination, so the search page
      // never loads and the search would go unrecorded. PubSub is synchronous,
      // so subscribers run before the navigation below.
      //
      // Guarded because this is the shopper pressing Enter: an unguarded throw
      // here would skip the navigation and leave the key doing nothing.
      try {
        // Trimmed to match how the query was normalised to arm the redirect in
        // the first place - otherwise "jellycat " reports as a term distinct
        // from "jellycat".
        const input = this.form.querySelector('input[name="q"]');

        this.publish(WYATT.PubSub.events.searchRedirected, {
          searchTerm: input ? input.value.trim() : "",
          destination: redirect,
          source: redirectSource,
        });
      } catch (trackingError) {
        console.error(trackingError);
      }

      window.location.href = redirect;
    }
  },
);
